import { type ChangeEvent, useState } from "react";
import { buildReplayRoomPath, navigateTo } from "../../router";
import { createReplayShare } from "../services/game-api";

/**
 * Replay lobby — the single entry point into a replay room. Enter a share code
 * (auto-filled when arriving via a /replay/room?code= link) or upload a
 * downloaded game-export file to mint a fresh share. Both resolve to the same
 * replay room. Never touches live game data.
 */
const inputClass =
  "w-full rounded-md border border-border-primary bg-surface-primary px-3 py-2 text-sm text-text-primary";
const primaryBtn =
  "rounded-md bg-accent-blue px-4 py-2 font-semibold text-sm text-white disabled:opacity-40";
const secondaryBtn =
  "rounded-md border border-border-primary px-4 py-2 font-semibold text-sm text-text-primary disabled:opacity-40";

export function ReplayLobbyScreen() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [createdLink, setCreatedLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  function openByCode() {
    const trimmed = code.trim();
    if (trimmed) navigateTo(buildReplayRoomPath(trimmed));
  }

  async function onFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    setError(null);
    setCreatedLink(null);
    try {
      const parsed = JSON.parse(await file.text()) as {
        gameType?: string;
        events?: readonly unknown[];
      };
      if (!parsed.gameType || !Array.isArray(parsed.events)) {
        throw new Error("올바른 리플레이 파일이 아닙니다");
      }
      const res = await createReplayShare({
        gameType: parsed.gameType,
        events: parsed.events,
      });
      if (!res.success || !res.data) {
        throw new Error(res.error ?? "공유 링크 생성에 실패했습니다");
      }
      const origin =
        typeof window === "undefined" ? "" : window.location.origin;
      setCreatedLink(`${origin}${buildReplayRoomPath(res.data.code)}`);
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : "파일을 불러올 수 없습니다",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="min-h-screen bg-surface-primary p-6 text-text-primary"
      data-testid="replay-lobby-screen"
    >
      <div className="mx-auto flex max-w-md flex-col gap-6">
        <header>
          <h1 className="font-semibold text-xl">게임 리플레이</h1>
          <p className="mt-1 text-sm text-text-secondary">
            공유 코드를 입력하거나 저장한 리플레이 파일을 올려 다시 보세요.
          </p>
        </header>

        <section className="flex flex-col gap-2">
          <label className="font-semibold text-sm" htmlFor="replay-code">
            공유 코드
          </label>
          <input
            id="replay-code"
            className={inputClass}
            data-testid="replay-lobby-code-input"
            value={code}
            placeholder="예: A1B2C3D4E5"
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") openByCode();
            }}
          />
          <button
            type="button"
            className={primaryBtn}
            data-testid="replay-lobby-open"
            disabled={code.trim() === ""}
            onClick={openByCode}
          >
            리플레이 보기
          </button>
        </section>

        <div className="border-border-primary border-t" />

        <section className="flex flex-col gap-2">
          <span className="font-semibold text-sm">
            리플레이 파일로 공유 만들기
          </span>
          <input
            type="file"
            accept="application/json,.json"
            className="text-sm text-text-secondary"
            data-testid="replay-lobby-file"
            disabled={busy}
            onChange={onFile}
          />
          {createdLink && (
            <div
              className="flex flex-col gap-2 rounded-md border border-border-primary bg-surface-secondary p-3"
              data-testid="replay-lobby-created-link"
            >
              <span className="font-semibold text-text-secondary text-xs">
                공유 링크가 생성되었습니다
              </span>
              <input
                readOnly
                className={inputClass}
                data-testid="replay-lobby-created-link-input"
                value={createdLink}
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                className={secondaryBtn}
                onClick={() =>
                  navigateTo(
                    new URL(createdLink).pathname + new URL(createdLink).search,
                  )
                }
              >
                리플레이 보기
              </button>
            </div>
          )}
          {error && (
            <p
              className="text-sm text-status-error"
              data-testid="replay-lobby-error"
            >
              {error}
            </p>
          )}
        </section>

        <button
          type="button"
          className={secondaryBtn}
          onClick={() => navigateTo("/")}
        >
          홈으로
        </button>
      </div>
    </div>
  );
}
